import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Subscription } from "rxjs";
import {
  BackgroundUploadService,
  UploadTask,
} from "../../../services/background-upload.service";

@Component({
  selector: "app-upload-progress",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="upload-notifications" *ngIf="activeTasks.length > 0">
      <div
        class="upload-task"
        *ngFor="let task of activeTasks"
        [ngClass]="'status-' + task.status"
      >
        <div class="task-header">
          <div class="task-info">
            <i
              class="fas"
              [ngClass]="{
                'fa-cloud-upload-alt': task.status === 'uploading',
                'fa-cog fa-spin': task.status === 'processing',
                'fa-check-circle': task.status === 'completed',
                'fa-exclamation-triangle': task.status === 'error'
              }"
            ></i>
            <span class="task-title">{{ task.title }}</span>
          </div>
          <button
            class="btn-close"
            (click)="closeTask(task.id)"
            *ngIf="task.status === 'error'"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="task-status">
          <span class="status-text">
            {{ getStatusText(task) }}
          </span>
        </div>

        <div class="progress-bar" *ngIf="task.status === 'uploading'">
          <div class="progress-fill" [style.width.%]="task.progress"></div>
        </div>

        <div class="error-message" *ngIf="task.status === 'error'">
          {{ task.error }}
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .upload-notifications {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1050;
        max-width: 350px;
      }

      .upload-task {
        background: white;
        border-radius: 8px;
        padding: 15px;
        margin-bottom: 10px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        border-left: 4px solid #0389c9;
        animation: slideIn 0.3s ease-out;
      }

      .upload-task.status-error {
        border-left-color: #dc3545;
      }

      .upload-task.status-completed {
        border-left-color: #28a745;
      }

      .task-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
      }

      .task-info {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .task-info i {
        color: #0389c9;
        font-size: 1.1rem;
      }

      .status-error .task-info i {
        color: #dc3545;
      }

      .status-completed .task-info i {
        color: #28a745;
      }

      .task-title {
        font-weight: 500;
        color: #333;
        font-size: 0.9rem;
      }

      .btn-close {
        background: none;
        border: none;
        color: #999;
        cursor: pointer;
        padding: 2px;
        border-radius: 3px;
      }

      .btn-close:hover {
        background: #f8f9fa;
        color: #666;
      }

      .task-status {
        margin-bottom: 8px;
      }

      .status-text {
        font-size: 0.85rem;
        color: #666;
      }

      .progress-bar {
        height: 4px;
        background: #e9ecef;
        border-radius: 2px;
        overflow: hidden;
      }

      .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #0389c9, #0277b3);
        transition: width 0.3s ease;
      }

      .error-message {
        font-size: 0.8rem;
        color: #dc3545;
        margin-top: 5px;
      }

      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      @media (max-width: 768px) {
        .upload-notifications {
          top: 10px;
          right: 10px;
          left: 10px;
          max-width: none;
        }
      }
    `,
  ],
})
export class UploadProgressComponent implements OnInit, OnDestroy {
  activeTasks: UploadTask[] = [];
  private subscription?: Subscription;

  constructor(private uploadService: BackgroundUploadService) {}

  ngOnInit() {
    this.subscription = this.uploadService.uploadTasks$.subscribe((tasks) => {
      this.activeTasks = tasks.filter(
        (task) =>
          task.status !== "completed" ||
          (task.status === "completed" &&
            Date.now() - parseInt(task.id.split("_")[1]) < 3000)
      );
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  getStatusText(task: UploadTask): string {
    switch (task.status) {
      case "uploading":
        return `Upload en cours... ${task.progress}%`;
      case "processing":
        return "Traitement en cours...";
      case "completed":
        return "Upload terminé !";
      case "error":
        return "Erreur d'upload";
      default:
        return "";
    }
  }

  closeTask(taskId: string) {
    this.uploadService.cancelTask(taskId);
  }
}
